// src/QuadratPreprocessingPage.js
import React, { useState, createRef, useEffect, useCallback, useRef } from "react";
import "./QuadratPreprocessingPage.css";
import Cropper from "react-easy-crop";
import LargeTileProcessing from "./LargeTileProcessing.js";

// Enhanced library loading with proper async handling
const loadRequiredLibraries = async () => {
  const librariesToLoad = [
    {
      name: 'Pako',
      url: 'https://cdn.jsdelivr.net/npm/pako@2.1.0/dist/pako.min.js',
      check: () => window.pako
    },
    {
      name: 'UTIF',
      urls: [
        'https://cdn.jsdelivr.net/gh/photopea/UTIF.js/UTIF.js',
        'https://cdn.jsdelivr.net/npm/utif@3.1.0/UTIF.js',
        'https://unpkg.com/utif@3.1.0/UTIF.js'
      ],
      check: () => window.UTIF
    },
    {
      name: 'EXIF',
      url: 'https://cdn.jsdelivr.net/npm/exif-js@2.3.0/exif.js',
      check: () => window.EXIF
    }
  ];

  for (const lib of librariesToLoad) {
    if (!lib.check()) {
      console.log(`Loading ${lib.name} library...`);
      
      const urls = lib.urls || [lib.url];
      let loaded = false;
      
      for (const url of urls) {
        try {
          await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = url;
            script.onload = () => {
              console.log(`${lib.name} library loaded successfully from ${url}`);
              loaded = true;
              resolve();
            };
            script.onerror = () => {
              console.warn(`Failed to load ${lib.name} from ${url}`);
              reject(new Error(`Failed to load from ${url}`));
            };
            document.head.appendChild(script);
          });
          break;
        } catch (error) {
          console.warn(`Attempt failed for ${lib.name} from ${url}:`, error);
          if (url === urls[urls.length - 1]) {
            throw new Error(`Failed to load ${lib.name} from all sources`);
          }
        }
      }
    }
  }
  
  console.log('All libraries loaded successfully');
};

// Enhanced TIFF processing function
const processTiffFile = async (file) => {
  return new Promise((resolve, reject) => {
    if (!window.UTIF) {
      reject(new Error('UTIF library not loaded. Please wait for libraries to load and try again.'));
      return;
    }
    
    const reader = new FileReader();
    reader.onload = function(e) {
      try {
        const buffer = e.target.result;
        console.log(`Processing TIFF: ${file.name}, size: ${buffer.byteLength} bytes`);
        
        let ifds;
        try {
          ifds = window.UTIF.decode(buffer);
          console.log(`TIFF decode successful. Found ${ifds.length} image(s)`);
        } catch (decodeError) {
          console.error('TIFF decode failed:', decodeError);
          throw new Error(`TIFF processing failed: ${decodeError.message}`);
        }
        
        if (!ifds || ifds.length === 0) {
          throw new Error('No image data found in TIFF file');
        }
        
        let ifd = null;
        let validIfdIndex = -1;
        
        for (let i = 0; i < ifds.length; i++) {
          const currentIfd = ifds[i];
          let width = currentIfd.width;
          let height = currentIfd.height;
          
          if (!width && currentIfd.t256) {
            width = Array.isArray(currentIfd.t256) ? currentIfd.t256[0] : currentIfd.t256;
          }
          if (!height && currentIfd.t257) {
            height = Array.isArray(currentIfd.t257) ? currentIfd.t257[0] : currentIfd.t257;
          }
          
          if (width && height && width > 0 && height > 0) {
            currentIfd.width = width;
            currentIfd.height = height;
            ifd = currentIfd;
            validIfdIndex = i;
            break;
          }
        }
        
        if (!ifd) {
          throw new Error(`No valid image dimensions found in TIFF file.`);
        }
        
        try {
          window.UTIF.decodeImage(buffer, ifd);
          console.log('TIFF image decoded successfully');
        } catch (imageDecodeError) {
          console.error('Image decode failed:', imageDecodeError);
          throw new Error(`Failed to decode TIFF image data: ${imageDecodeError.message}`);
        }
        
        let rgba;
        try {
          rgba = window.UTIF.toRGBA8(ifd);
          if (!rgba || rgba.length === 0) {
            throw new Error('Failed to convert TIFF to RGBA data');
          }
          console.log(`RGBA conversion successful, ${rgba.length} bytes`);
        } catch (rgbaError) {
          console.error('RGBA conversion failed:', rgbaError);
          throw new Error(`Failed to convert TIFF to displayable format: ${rgbaError.message}`);
        }
        
        // Extract metadata including GeoTIFF tags
        const metadata = {
          width: ifd.width,
          height: ifd.height,
          geoTags: {},
          exifData: {},
          fileInfo: {
            name: file.name,
            size: file.size,
            lastModified: file.lastModified,
            compression: ifd.t259?.[0] || 'unknown',
            bitsPerSample: ifd.t258?.[0] || 'unknown'
          }
        };
        
        // Extract GeoTIFF tags
        if (ifd.t33550 || ifd.t33922 || ifd.t34735 || ifd.t34736 || ifd.t34737) {
          console.log('Extracting GeoTIFF metadata...');
          const geoTags = [33922, 33550, 34735, 34736, 34737];
          geoTags.forEach(tag => {
            const tagKey = `t${tag}`;
            if (ifd[tagKey]) {
              metadata.geoTags[tag] = ifd[tagKey];
            }
          });
        }
        // Create canvas and convert to blob
        const canvas = document.createElement('canvas');
        canvas.width = ifd.width;
        canvas.height = ifd.height;
        const ctx = canvas.getContext('2d');
        try {
          const imageData = ctx.createImageData(canvas.width, canvas.height);
          imageData.data.set(rgba);
          ctx.putImageData(imageData, 0, 0);
          
          canvas.toBlob((blob) => {
            if (blob) {
              const url = URL.createObjectURL(blob);
              console.log('TIFF processing completed successfully');
              resolve({ url, metadata, originalBuffer: buffer });
            } else {
              reject(new Error('Failed to create blob from processed TIFF'));
            }
          }, 'image/png', 0.95);
          
        } catch (canvasError) {
          console.error('Canvas processing failed:', canvasError);
          reject(new Error(`Failed to create displayable image: ${canvasError.message}`));
        }
        
      } catch (error) {
        console.error('TIFF processing error:', error);
        reject(new Error(error.message));
      }
    };
    
    reader.onerror = (error) => {
      console.error('File reader error:', error);
      reject(new Error('Failed to read TIFF file'));
    };
    
    reader.readAsArrayBuffer(file);
  });
};

// Calculate real-world coordinates from pixel coordinates using GeoTIFF metadata
const pixelToGeoCoordinates = (pixelX, pixelY, geoTags) => {
  if (!geoTags || !geoTags[33550] || !geoTags[33922]) {
    return null;
  }

  const pixelScale = geoTags[33550];
  const tiePoint = geoTags[33922];
  
  if (!pixelScale || !tiePoint || pixelScale.length < 2 || tiePoint.length < 6) {
    return null;
  }

  const geoX = tiePoint[3] + (pixelX - tiePoint[0]) * pixelScale[0];
  const geoY = tiePoint[4] - (pixelY - tiePoint[1]) * pixelScale[1];
  
  return {
    longitude: geoX,
    latitude: geoY,
    coordinateSystem: 'projected',
    pixelScale: {
      x: pixelScale[0],
      y: pixelScale[1]
    }
  };
};

// Calculate smart position for quadrat controls
const calculateSmartPosition = (quadrat, imageDimensions, containerRect) => {
  if (!containerRect || !imageDimensions) return { top: -35, left: 0 };
  
  const scaleX = containerRect.width / imageDimensions.width;
  const scaleY = containerRect.height / imageDimensions.height;
  
  const quadratLeft = quadrat.x * scaleX;
  const quadratTop = quadrat.y * scaleY;
  const quadratRight = (quadrat.x + quadrat.width) * scaleX;
  const quadratBottom = (quadrat.y + quadrat.height) * scaleY;
  
  // Default position (above quadrat)
  let position = { top: -35, left: 0 };
  
  // Check if controls would be cut off at the top
  if (quadratTop < 40) {
    // Place controls inside at the top
    position = { top: 5, left: 5 };
  }
  
  // Check if controls would be cut off on the left
  if (quadratLeft < 10) {
    position.left = 5;
  }
  
  // Check if controls would be cut off on the right
  if (quadratRight > containerRect.width - 150) {
    position.left = quadrat.width * scaleX - 150;
  }
  
  return position;
};

// Debounce function for real-time preview
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  
  return debouncedValue;
};

const QuadratPreprocessingPage = () => {
  // Library loading state
  const [librariesLoaded, setLibrariesLoaded] = useState(false);
  const [libraryError, setLibraryError] = useState(null);
  
  // Main image state
  const [originalImage, setOriginalImage] = useState(null);
  const [processedImage, setProcessedImage] = useState(null);
  const [imageMetadata, setImageMetadata] = useState(null);
  const [imageDimensions, setImageDimensions] = useState(null);
  const [originalFile, setOriginalFile] = useState(null);
  
  // Quadrat selection state
  const [quadratSize, setQuadratSize] = useState({ width: 512, height: 512 });
  const [customSize, setCustomSize] = useState(false);
  const [selectedQuadrats, setSelectedQuadrats] = useState([]);
  const [isSelectingQuadrats, setIsSelectingQuadrats] = useState(false);
  
  // Processing modal state
  const [processingModalOpen, setProcessingModalOpen] = useState(false);
  const [currentQuadratIndex, setCurrentQuadratIndex] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [largeTileModalOpen, setLargeTileModalOpen] = useState(false);
  // Enhanced transformation state
  const [transformations, setTransformations] = useState({
    rotation: 0,
    scale: 1,
    flipHorizontal: false,
    flipVertical: false
  });
  
  // Enhanced color correction state with underwater-specific filters
  const [colorCorrections, setColorCorrections] = useState({
    // Basic corrections
    brightness: 0,
    contrast: 0,
    saturation: 0,
    gamma: 1,
    
    // Underwater-specific corrections
    redEnhancement: 0,
    blueReduction: 0,
    greenBalance: 0,
    depthCompensation: 0,
    turbidityReduction: 0,
    particleReduction: 0,
    colorTemperature: 0,
    tint: 0,
    clarity: 0,
    dehaze: 0,
    
    // Advanced filters
    adaptiveHistogram: false,
    colorGrading: false,
    noiseReduction: 0
  });
  
  // Active tab in modal
  const [activeTab, setActiveTab] = useState('transform');
  
  // Real-time preview state
  const [previewCanvas, setPreviewCanvas] = useState(null);
  const [isApplyingPreview, setIsApplyingPreview] = useState(false);
  const previewCanvasRef = useRef(null);
  const previewWorkerRef = useRef(null);
  
  // Debounced values for performance
  const debouncedCorrections = useDebounce(colorCorrections, 100);
  const debouncedTransformations = useDebounce(transformations, 100);
  
  // Export settings
  const [exportFormat, setExportFormat] = useState('original');
  const [exportQuality, setExportQuality] = useState(95);
  
  // Processing state
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Container ref for smart positioning
  const imageContainerRef = useRef(null);
  
  const fileInputRef = createRef();

  // Load libraries on component mount
  useEffect(() => {
    const initializeLibraries = async () => {
      try {
        console.log('Initializing required libraries...');
        await loadRequiredLibraries();
        setLibrariesLoaded(true);
        console.log('All libraries ready for use');
      } catch (error) {
        console.error('Library loading failed:', error);
        setLibraryError(error.message);
      }
    };
    
    initializeLibraries();
  }, []);

  // Apply all image corrections with real-time preview
  const applyAllCorrections = useCallback((canvas, corrections, transformations, quadrat) => {
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    
    // Apply underwater-specific corrections
    for (let i = 0; i < data.length; i += 4) {
      let r = data[i];
      let g = data[i + 1];
      let b = data[i + 2];
      
      // Red Enhancement (compensate for red absorption)
      if (corrections.redEnhancement !== 0) {
        r = Math.min(255, r * (1 + corrections.redEnhancement * 0.5));
      }
      
      // Blue Reduction (reduce blue dominance)
      if (corrections.blueReduction !== 0) {
        b = Math.max(0, b * (1 - corrections.blueReduction * 0.3));
      }
      
      // Green Balance
      if (corrections.greenBalance !== 0) {
        g = Math.min(255, Math.max(0, g + corrections.greenBalance * 30));
      }
      
      // Depth Compensation (simulate light restoration at depth)
      if (corrections.depthCompensation !== 0) {
        const depth = corrections.depthCompensation;
        r = Math.min(255, r * (1 + depth * 0.6));
        g = Math.min(255, g * (1 + depth * 0.3));
        b = Math.max(0, b * (1 - depth * 0.1));
      }
      
      // Turbidity Reduction
      if (corrections.turbidityReduction !== 0) {
        const factor = corrections.turbidityReduction;
        const avg = (r + g + b) / 3;
        
        r = r + (r - avg) * factor * 0.5;
        g = g + (g - avg) * factor * 0.5;
        b = b + (b - avg) * factor * 0.5;
        
        r = Math.max(0, Math.min(255, r));
        g = Math.max(0, Math.min(255, g));
        b = Math.max(0, Math.min(255, b));
      }
      
      // Color Temperature
      if (corrections.colorTemperature !== 0) {
        const temp = corrections.colorTemperature;
        r = Math.min(255, Math.max(0, r + temp * 30));
        b = Math.min(255, Math.max(0, b - temp * 30));
      }
      
      // Tint
      if (corrections.tint !== 0) {
        g = Math.min(255, Math.max(0, g + corrections.tint * 20));
      }
      
      // Clarity (local contrast enhancement)
      if (corrections.clarity !== 0) {
        const factor = 1 + corrections.clarity * 0.3;
        const avg = (r + g + b) / 3;
        r = avg + (r - avg) * factor;
        g = avg + (g - avg) * factor;
        b = avg + (b - avg) * factor;
        
        r = Math.max(0, Math.min(255, r));
        g = Math.max(0, Math.min(255, g));
        b = Math.max(0, Math.min(255, b));
      }
      
      // Basic Corrections
      // Brightness
      if (corrections.brightness !== 0) {
        r = Math.max(0, Math.min(255, r + corrections.brightness));
        g = Math.max(0, Math.min(255, g + corrections.brightness));
        b = Math.max(0, Math.min(255, b + corrections.brightness));
      }
      
      // Contrast
      if (corrections.contrast !== 0) {
        const factor = (259 * (corrections.contrast + 255)) / (255 * (259 - corrections.contrast));
        r = Math.max(0, Math.min(255, factor * (r - 128) + 128));
        g = Math.max(0, Math.min(255, factor * (g - 128) + 128));
        b = Math.max(0, Math.min(255, factor * (b - 128) + 128));
      }
      
      // Saturation
      if (corrections.saturation !== 0) {
        const gray = 0.299 * r + 0.587 * g + 0.114 * b;
        r = Math.max(0, Math.min(255, gray + corrections.saturation * (r - gray)));
        g = Math.max(0, Math.min(255, gray + corrections.saturation * (g - gray)));
        b = Math.max(0, Math.min(255, gray + corrections.saturation * (b - gray)));
      }
      
      // Gamma
      if (corrections.gamma !== 1) {
        r = Math.pow(r / 255, 1 / corrections.gamma) * 255;
        g = Math.pow(g / 255, 1 / corrections.gamma) * 255;
        b = Math.pow(b / 255, 1 / corrections.gamma) * 255;
      }
      
      // Dehaze
      if (corrections.dehaze !== 0) {
        const haze = corrections.dehaze * 0.02;
        r = (r - 255 * haze) / (1 - haze);
        g = (g - 255 * haze) / (1 - haze);
        b = (b - 255 * haze) / (1 - haze);
        
        r = Math.max(0, Math.min(255, r));
        g = Math.max(0, Math.min(255, g));
        b = Math.max(0, Math.min(255, b));
      }
      
      data[i] = r;
      data[i + 1] = g;
      data[i + 2] = b;
    }
    
    // Apply particle reduction filter
    if (corrections.particleReduction > 0) {
      const radius = Math.ceil(corrections.particleReduction);
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = canvas.width;
      tempCanvas.height = canvas.height;
      const tempCtx = tempCanvas.getContext('2d');
      tempCtx.putImageData(imageData, 0, 0);
      
      // Apply bilateral filter approximation
      ctx.filter = `blur(${radius}px)`;
      ctx.drawImage(tempCanvas, 0, 0);
      ctx.filter = 'none';
      
      // Blend with original
      const blendedData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const alpha = corrections.particleReduction / 10;
      
      for (let i = 0; i < data.length; i += 4) {
        data[i] = data[i] * (1 - alpha) + blendedData.data[i] * alpha;
        data[i + 1] = data[i + 1] * (1 - alpha) + blendedData.data[i + 1] * alpha;
        data[i + 2] = data[i + 2] * (1 - alpha) + blendedData.data[i + 2] * alpha;
      }
    }
    
    ctx.putImageData(imageData, 0, 0);
    
    // Apply transformations
    if (transformations.flipHorizontal || transformations.flipVertical || 
        transformations.rotation !== 0 || transformations.scale !== 1) {
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = canvas.width;
      tempCanvas.height = canvas.height;
      const tempCtx = tempCanvas.getContext('2d');
      tempCtx.drawImage(canvas, 0, 0);
      
      ctx.save();
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Move to center for transformations
      ctx.translate(canvas.width / 2, canvas.height / 2);
      
      // Apply scale
      const scaleX = transformations.flipHorizontal ? -transformations.scale : transformations.scale;
      const scaleY = transformations.flipVertical ? -transformations.scale : transformations.scale;
      ctx.scale(scaleX, scaleY);
      
      // Apply rotation
      ctx.rotate((transformations.rotation * Math.PI) / 180);
      
      // Draw image centered
      ctx.drawImage(tempCanvas, -canvas.width / 2, -canvas.height / 2);
      
      ctx.restore();
    }
  }, []);

  // Update preview in real-time
  useEffect(() => {
    if (!processingModalOpen || currentQuadratIndex === null || !processedImage) return;
    
    const updatePreview = async () => {
      setIsApplyingPreview(true);
      
      try {
        const quadrat = selectedQuadrats[currentQuadratIndex];
        if (!quadrat || !previewCanvasRef.current) return;
        
        const canvas = previewCanvasRef.current;
        canvas.width = quadrat.width;
        canvas.height = quadrat.height;
        const ctx = canvas.getContext('2d');
        
        // Load and draw the quadrat area
        const img = new Image();
        img.crossOrigin = "Anonymous";
        img.src = processedImage;
        
        await new Promise((resolve) => {
          img.onload = resolve;
        });
        
        ctx.drawImage(
          img,
          quadrat.x, quadrat.y, quadrat.width, quadrat.height,
          0, 0, quadrat.width, quadrat.height
        );
        
        // Apply all corrections
        applyAllCorrections(canvas, debouncedCorrections, debouncedTransformations, quadrat);
        
      } catch (error) {
        console.error('Error updating preview:', error);
      } finally {
        setIsApplyingPreview(false);
      }
    };
    
    updatePreview();
  }, [processingModalOpen, currentQuadratIndex, processedImage, debouncedCorrections, debouncedTransformations, selectedQuadrats, applyAllCorrections]);

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!librariesLoaded) {
      alert('Please wait for libraries to load before uploading images.');
      return;
    }
    
    console.log(`Processing file: ${file.name}, type: ${file.type}, size: ${file.size} bytes`);
    
    try {
      let previewURL;
      let metadata = null;
      let originalBuffer = null;
      
      const fileExtension = file.name.toLowerCase().split('.').pop();
      const isTiff = file.type === 'image/tiff' || 
                    file.type === 'image/tif' || 
                    fileExtension === 'tiff' || 
                    fileExtension === 'tif';
      
      if (isTiff) {
        console.log('Processing TIFF file with metadata preservation...');
        const tiffData = await processTiffFile(file);
        previewURL = tiffData.url;
        metadata = tiffData.metadata;
        originalBuffer = tiffData.originalBuffer;
        console.log('TIFF metadata extracted:', metadata);
      } else {
        previewURL = URL.createObjectURL(file);
        
        if (window.EXIF && (file.type === 'image/jpeg' || fileExtension === 'jpg' || fileExtension === 'jpeg')) {
          try {
            await new Promise((resolve) => {
              window.EXIF.getData(file, function() {
                const lat = window.EXIF.getTag(this, "GPSLatitude");
                const lon = window.EXIF.getTag(this, "GPSLongitude");
                if (lat && lon) {
                  metadata = {
                    exifData: {
                      gps: {
                        lat: lat,
                        lon: lon,
                        latRef: window.EXIF.getTag(this, "GPSLatitudeRef"),
                        lonRef: window.EXIF.getTag(this, "GPSLongitudeRef")
                      }
                    }
                  };
                }
                resolve();
              });
            });
          } catch (error) {
            console.warn('Could not extract EXIF data:', error);
          }
        }
      }

      const img = new Image();
      img.src = previewURL;
      img.onload = () => {
        const dims = { 
          width: img.naturalWidth, 
          height: img.naturalHeight 
        };
        
        setOriginalFile(file);
        setOriginalImage(previewURL);
        setProcessedImage(previewURL);
        setImageMetadata(metadata);
        setImageDimensions(dims);
        setSelectedQuadrats([]);
        
        // Set default export format to original
        setExportFormat(fileExtension);
        
        console.log('Image loaded successfully:', dims);
      };
      
    } catch (error) {
      console.error('Error processing file:', error);
      alert(`Error processing ${file.name}: ${error.message}`);
    }
  };

  // Handle quadrat selection on image click
  const handleImageClick = (event) => {
    if (!isSelectingQuadrats || !imageDimensions) return;
    
    const rect = event.target.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const clickY = event.clientY - rect.top;
    
    const scaleX = imageDimensions.width / rect.width;
    const scaleY = imageDimensions.height / rect.height;
    
    const centerX = Math.floor(clickX * scaleX);
    const centerY = Math.floor(clickY * scaleY);
    
    // Calculate quadrat bounds
    const halfWidth = Math.floor(quadratSize.width / 2);
    const halfHeight = Math.floor(quadratSize.height / 2);
    
    const x = Math.max(0, Math.min(centerX - halfWidth, imageDimensions.width - quadratSize.width));
    const y = Math.max(0, Math.min(centerY - halfHeight, imageDimensions.height - quadratSize.height));
    
    const newQuadrat = {
      id: Date.now(),
      x: x,
      y: y,
      width: quadratSize.width,
      height: quadratSize.height,
      processed: false,
      colorCorrections: { ...colorCorrections },
      transformations: { ...transformations }
    };
    
    setSelectedQuadrats(prev => [...prev, newQuadrat]);
  };

  // Remove quadrat
  const removeQuadrat = (quadratId) => {
    setSelectedQuadrats(prev => prev.filter(q => q.id !== quadratId));
  };

  // Open processing modal for specific quadrat
  const openProcessingModal = (quadratIndex) => {
    setCurrentQuadratIndex(quadratIndex);
    setProcessingModalOpen(true);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setActiveTab('transform');
    
    // Set corrections to quadrat's current values
    const quadrat = selectedQuadrats[quadratIndex];
    setColorCorrections(quadrat.colorCorrections);
    setTransformations(quadrat.transformations || {
      rotation: 0,
      scale: 1,
      flipHorizontal: false,
      flipVertical: false
    });
  };

  // Handle crop complete
  const handleCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  // Apply preprocessing to current quadrat
  const applyPreprocessing = async () => {
    if (currentQuadratIndex === null) return;
    
    setIsProcessing(true);
    
    try {
      const quadrat = selectedQuadrats[currentQuadratIndex];
      
      // Create canvas for the quadrat
      const canvas = document.createElement('canvas');
      canvas.width = quadrat.width;
      canvas.height = quadrat.height;
      const ctx = canvas.getContext('2d');
      
      // Load the processed image
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.src = processedImage;
      
      await new Promise((resolve) => {
        img.onload = resolve;
      });
      
      // Draw the quadrat area
      ctx.drawImage(
        img,
        quadrat.x, quadrat.y, quadrat.width, quadrat.height,
        0, 0, quadrat.width, quadrat.height
      );
      
      // Apply all corrections
      applyAllCorrections(canvas, colorCorrections, transformations, quadrat);
      
      // Update quadrat with processed data
      const updatedQuadrats = [...selectedQuadrats];
      updatedQuadrats[currentQuadratIndex] = {
        ...quadrat,
        processed: true,
        colorCorrections: { ...colorCorrections },
        transformations: { ...transformations },
        processedCanvas: canvas
      };
      
      setSelectedQuadrats(updatedQuadrats);
      setProcessingModalOpen(false);
      setCurrentQuadratIndex(null);
      
    } catch (error) {
      console.error('Error applying preprocessing:', error);
      alert('Error applying preprocessing. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Reset all corrections
  const resetAllCorrections = () => {
    setColorCorrections({
      brightness: 0,
      contrast: 0,
      saturation: 0,
      gamma: 1,
      redEnhancement: 0,
      blueReduction: 0,
      greenBalance: 0,
      depthCompensation: 0,
      turbidityReduction: 0,
      particleReduction: 0,
      colorTemperature: 0,
      tint: 0,
      clarity: 0,
      dehaze: 0,
      adaptiveHistogram: false,
      colorGrading: false,
      noiseReduction: 0
    });
    
    setTransformations({
      rotation: 0,
      scale: 1,
      flipHorizontal: false,
      flipVertical: false
    });
  };

  // Apply underwater presets
  const applyPreset = (presetName) => {
    switch (presetName) {
      case 'shallow':
        setColorCorrections(prev => ({
          ...prev,
          redEnhancement: 0.3,
          blueReduction: 0.2,
          greenBalance: 0.1,
          depthCompensation: 0.2,
          turbidityReduction: 0.3,
          brightness: 10,
          contrast: 15,
          saturation: 0.2,
          gamma: 1.1,
          clarity: 0.3,
          dehaze: 0.2
        }));
        break;
        
      case 'deep':
        setColorCorrections(prev => ({
          ...prev,
          redEnhancement: 0.6,
          blueReduction: 0.3,
          greenBalance: 0.2,
          depthCompensation: 0.6,
          turbidityReduction: 0.4,
          brightness: 20,
          contrast: 25,
          saturation: 0.3,
          gamma: 1.3,
          clarity: 0.4,
          dehaze: 0.4,
          particleReduction: 2
        }));
        break;
        
      case 'turbid':
        setColorCorrections(prev => ({
          ...prev,
          redEnhancement: 0.4,
          blueReduction: 0.1,
          greenBalance: -0.2,
          turbidityReduction: 0.7,
          particleReduction: 4,
          brightness: 15,
          contrast: 30,
          saturation: 0.1,
          gamma: 1.2,
          clarity: 0.6,
          dehaze: 0.6,
          noiseReduction: 3
        }));
        break;
        
      case 'green-water':
        setColorCorrections(prev => ({
          ...prev,
          redEnhancement: 0.5,
          blueReduction: 0.2,
          greenBalance: -0.4,
          turbidityReduction: 0.5,
          colorTemperature: 0.3,
          tint: -0.3,
          brightness: 10,
          contrast: 20,
          saturation: 0.15,
          gamma: 1.15,
          clarity: 0.4,
          dehaze: 0.3
        }));
        break;
    }
  };

  // Export all processed quadrats
  const exportAllQuadrats = async () => {
    const processedQuadrats = selectedQuadrats.filter(q => q.processed);
    
    if (processedQuadrats.length === 0) {
      alert('No processed quadrats to export. Please process at least one quadrat first.');
      return;
    }
    
    for (let i = 0; i < processedQuadrats.length; i++) {
      const quadrat = processedQuadrats[i];
      
      // Determine export format
      let format = exportFormat;
      if (format === 'original' && originalFile) {
        format = originalFile.name.toLowerCase().split('.').pop();
      }
      
      // Convert canvas to blob
      const canvas = quadrat.processedCanvas;
      const blob = await new Promise((resolve) => {
        if (format === 'jpg' || format === 'jpeg') {
          canvas.toBlob(resolve, 'image/jpeg', exportQuality / 100);
        } else if (format === 'png') {
          canvas.toBlob(resolve, 'image/png');
        } else {
          canvas.toBlob(resolve, 'image/png');
        }
      });
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const baseName = originalFile?.name.replace(/\.[^/.]+$/, '') || 'quadrat';
      link.download = `${baseName}_quadrat_${i + 1}_${timestamp}.${format}`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      // Small delay between downloads
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    console.log(`Exported ${processedQuadrats.length} quadrats`);
  };

  // Export metadata for all quadrats
  const exportMetadata = () => {
    const processedQuadrats = selectedQuadrats.filter(q => q.processed);
    
    if (processedQuadrats.length === 0) {
      alert('No processed quadrats to export metadata for.');
      return;
    }
    
    const metadata = {
      timestamp: new Date().toISOString(),
      originalFile: {
        name: originalFile?.name,
        size: originalFile?.size,
        type: originalFile?.type
      },
      imageDimensions: imageDimensions,
      geoLocation: imageMetadata?.geoTags || imageMetadata?.exifData?.gps || null,
      quadrats: processedQuadrats.map((quadrat, index) => ({
        id: quadrat.id,
        index: index + 1,
        position: {
          x: quadrat.x,
          y: quadrat.y,
          width: quadrat.width,
          height: quadrat.height
        },
        colorCorrections: quadrat.colorCorrections,
        transformations: quadrat.transformations,
        geoCoordinates: imageMetadata?.geoTags ? 
          pixelToGeoCoordinates(quadrat.x + quadrat.width/2, quadrat.y + quadrat.height/2, imageMetadata.geoTags) : 
          null
      }))
    };
    
    const blob = new Blob([JSON.stringify(metadata, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const baseName = originalFile?.name.replace(/\.[^/.]+$/, '') || 'quadrats';
    link.download = `${baseName}_quadrats_metadata_${timestamp}.json`;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Show loading screen if libraries aren't ready
  if (!librariesLoaded) {
    return (
      <div className="quadrat-container">
        <div className="title-container">
          <h1>Quadrat Preprocessing Tool</h1>
        </div>
        <div className="loading-screen">
          {libraryError ? (
            <div style={{ textAlign: 'center' }}>
              <h3 style={{ color: '#ff6b6b' }}>⚠️ Library Loading Failed</h3>
              <p>{libraryError}</p>
              <button 
                onClick={() => window.location.reload()} 
                className="reload-btn"
              >
                Reload Page
              </button>
            </div>
          ) : (
            <div style={{ textAlign: 'center' }}>
              <div className="spinner"></div>
              <h3>Loading Image Processing Libraries...</h3>
              <p>Please wait while we load the required libraries for advanced image processing.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="quadrat-container">
      <div className="title-container">
        <h1>Quadrat Preprocessing Tool</h1>
      </div>
      
      <div className="upload-section">
  <h2>Upload Image</h2>
  <p>Upload large TIFF, JPEG, PNG images for quadrat preprocessing</p>
  <input
    type="file"
    accept="image/*,.tiff,.tif"
    ref={fileInputRef}
    onChange={handleFileUpload}
    className="file-input"
  />
  <button 
    onClick={() => fileInputRef.current.click()}
    className="upload-btn"
  >
    📁 Select Image
  </button>
  
  <div style={{ marginTop: '1rem' }}>
    <button 
      onClick={() => setLargeTileModalOpen(true)}
      className="upload-btn"
      style={{ background: 'linear-gradient(135deg, #ff9800, #f57c00)' }}
    >
      🔲 Large Tile Processing
    </button>
  </div>
</div>
      
      {originalImage && (
        <>
          <div className="image-info">
            <h3>Image Information</h3>
            <div className="info-grid">
              <div>📐 Dimensions: {imageDimensions.width} × {imageDimensions.height}px</div>
              <div>📁 Format: {originalFile?.name.split('.').pop().toUpperCase()}</div>
              <div>💾 Size: {(originalFile?.size / (1024*1024)).toFixed(2)} MB</div>
              {imageMetadata?.geoTags && Object.keys(imageMetadata.geoTags).length > 0 && (
                <div>🗺️ GeoTIFF: Geolocation data preserved</div>
              )}
              {imageMetadata?.exifData?.gps && (
                <div>📍 GPS: Location data available</div>
              )}
            </div>
          </div>

          <div className="quadrat-controls">
            <h3>Quadrat Selection</h3>
            <div className="size-controls">
              <label>Quadrat Size:</label>
              <div className="size-options">
                {!customSize ? (
                  <div className="preset-sizes">
                    {[256, 512, 1024, 2048].map(size => (
                      <button
                        key={size}
                        className={`size-btn ${quadratSize.width === size && quadratSize.height === size ? 'active' : ''}`}
                        onClick={() => setQuadratSize({width: size, height: size})}
                      >
                        {size}×{size}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="custom-size">
                    <input
                      type="number"
                      value={quadratSize.width}
                      onChange={(e) => setQuadratSize(prev => ({...prev, width: parseInt(e.target.value) || 256}))}
                      placeholder="Width"
                      min="50"
                      max={imageDimensions?.width || 2048}
                    />
                    <span>×</span>
                    <input
                      type="number"
                      value={quadratSize.height}
                      onChange={(e) => setQuadratSize(prev => ({...prev, height: parseInt(e.target.value) || 256}))}
                      placeholder="Height"
                      min="50"
                      max={imageDimensions?.height || 2048}
                    />
                  </div>
                )}
                <button
                  className="toggle-size-btn"
                  onClick={() => setCustomSize(!customSize)}
                >
                  {customSize ? '📏 Presets' : '⚙️ Custom'}
                </button>
              </div>
            </div>
            
            <div className="selection-controls">
              <button
                className={`select-btn ${isSelectingQuadrats ? 'active' : ''}`}
                onClick={() => setIsSelectingQuadrats(!isSelectingQuadrats)}
              >
                {isSelectingQuadrats ? '✅ Stop Selecting' : '🎯 Start Selecting Quadrats'}
              </button>
              <button
                className="clear-btn"
                onClick={() => setSelectedQuadrats([])}
                disabled={selectedQuadrats.length === 0}
              >
                🗑️ Clear All ({selectedQuadrats.length})
              </button>
            </div>
          </div>

          <div className="main-image-container" ref={imageContainerRef}>
            <div className="image-wrapper">
              <img
                src={processedImage}
                alt="Main image"
                onClick={handleImageClick}
                className={`main-image ${isSelectingQuadrats ? 'selecting' : ''}`}
              />
              
              {/* Render quadrat overlays with smart positioning */}
              {selectedQuadrats.map((quadrat, index) => {
                const containerRect = imageContainerRef.current?.querySelector('.image-wrapper')?.getBoundingClientRect();
                const smartPos = calculateSmartPosition(quadrat, imageDimensions, containerRect);
                
                return (
                  <div
                    key={quadrat.id}
                    className={`quadrat-overlay ${quadrat.processed ? 'processed' : ''}`}
                    style={{
                      left: `${(quadrat.x / imageDimensions.width) * 100}%`,
                      top: `${(quadrat.y / imageDimensions.height) * 100}%`,
                      width: `${(quadrat.width / imageDimensions.width) * 100}%`,
                      height: `${(quadrat.height / imageDimensions.height) * 100}%`,
                    }}
                  >
                    <div 
                      className="quadrat-controls-overlay"
                      style={{
                        top: `${smartPos.top}px`,
                        left: `${smartPos.left}px`
                      }}
                    >
                      <span className="quadrat-number">{index + 1}</span>
                      <button 
                        className="process-btn"
                        onClick={() => openProcessingModal(index)}
                        title="Process this quadrat"
                      >
                        {quadrat.processed ? '✏️' : '⚙️'}
                      </button>
                      <button 
                        className="remove-btn"
                        onClick={() => removeQuadrat(quadrat.id)}
                        title="Remove this quadrat"
                      >
                        ❌
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {isSelectingQuadrats && (
              <div className="selection-hint">
                💡 Click on the image to place quadrats. Current size: {quadratSize.width}×{quadratSize.height}px
              </div>
            )}
          </div>

          {selectedQuadrats.length > 0 && (
            <div className="quadrats-summary">
              <h3>Selected Quadrats ({selectedQuadrats.length})</h3>
              <div className="quadrats-grid">
                {selectedQuadrats.map((quadrat, index) => (
                  <div key={quadrat.id} className={`quadrat-card ${quadrat.processed ? 'processed' : ''}`}>
                    <div className="quadrat-header">
                      <span className="quadrat-title">Quadrat {index + 1}</span>
                      <span className={`status ${quadrat.processed ? 'processed' : 'pending'}`}>
                        {quadrat.processed ? '✅ Processed' : '⏳ Pending'}
                      </span>
                    </div>
                    <div className="quadrat-info">
                      <div>📍 Position: ({quadrat.x}, {quadrat.y})</div>
                      <div>📐 Size: {quadrat.width}×{quadrat.height}px</div>
                      {imageMetadata?.geoTags && (
                        <div>🌍 Geo: Available</div>
                      )}
                    </div>
                    <div className="quadrat-actions">
                      <button onClick={() => openProcessingModal(index)}>
                        {quadrat.processed ? 'Edit' : 'Process'}
                      </button>
                      <button onClick={() => removeQuadrat(quadrat.id)}>Remove</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="export-section">
            <h3>Export Settings</h3>
            <div className="export-controls">
              <div className="format-selection">
                <label>Export Format:</label>
                <select value={exportFormat} onChange={(e) => setExportFormat(e.target.value)}>
                  <option value="original">Original Format</option>
                  <option value="png">PNG</option>
                  <option value="jpg">JPEG</option>
                  <option value="tiff">TIFF</option>
                </select>
              </div>
              
              {(exportFormat === 'jpg' || exportFormat === 'jpeg') && (
                <div className="quality-control">
                  <label>JPEG Quality: {exportQuality}%</label>
                  <input
                    type="range"
                    min="50"
                    max="100"
                    value={exportQuality}
                    onChange={(e) => setExportQuality(parseInt(e.target.value))}
                  />
                </div>
              )}
              
              <div className="export-actions">
                <button
                  className="export-btn"
                  onClick={exportAllQuadrats}
                  disabled={selectedQuadrats.filter(q => q.processed).length === 0}
                >
                  📥 Export All Quadrats ({selectedQuadrats.filter(q => q.processed).length})
                </button>
                <button
                  className="metadata-btn"
                  onClick={exportMetadata}
                  disabled={selectedQuadrats.filter(q => q.processed).length === 0}
                >
                  📊 Export Metadata
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Enhanced Processing Modal with Real-time Preview */}
      {processingModalOpen && currentQuadratIndex !== null && (
        <div className="processing-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Process Quadrat {currentQuadratIndex + 1}</h3>
              <button 
                className="close-btn"
                onClick={() => setProcessingModalOpen(false)}
              >
                ✕
              </button>
            </div>
            
            <div className="modal-body">
              <div className="preview-section">
                <h4>Live Preview</h4>
                <div className="preview-container">
                  <canvas 
                    ref={previewCanvasRef}
                    className="preview-canvas"
                  />
                  {isApplyingPreview && (
                    <div className="preview-loading">
                      <div className="preview-spinner"></div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="controls-section">
                <div className="tabs">
                  <button 
                    className={`tab ${activeTab === 'transform' ? 'active' : ''}`}
                    onClick={() => setActiveTab('transform')}
                  >
                    Transform
                  </button>
                  <button 
                    className={`tab ${activeTab === 'basic' ? 'active' : ''}`}
                    onClick={() => setActiveTab('basic')}
                  >
                    Basic
                  </button>
                  <button 
                    className={`tab ${activeTab === 'underwater' ? 'active' : ''}`}
                    onClick={() => setActiveTab('underwater')}
                  >
                    Underwater
                  </button>
                  <button 
                    className={`tab ${activeTab === 'advanced' ? 'active' : ''}`}
                    onClick={() => setActiveTab('advanced')}
                  >
                    Advanced
                  </button>
                </div>
                
                <div className="tab-content">
                  {activeTab === 'transform' && (
                    <div className="transform-controls">
                      <h4>Transformations</h4>
                      
                      <div className="correction-group">
                        <label>Rotation: {transformations.rotation}°</label>
                        <input
                          type="range"
                          min="-180"
                          max="180"
                          step="1"
                          value={transformations.rotation}
                          onChange={(e) => setTransformations(prev => ({...prev, rotation: parseInt(e.target.value)}))}
                        />
                      </div>
                      
                      <div className="correction-group">
                        <label>Scale: {transformations.scale.toFixed(2)}x</label>
                        <input
                          type="range"
                          min="0.1"
                          max="3.0"
                          step="0.1"
                          value={transformations.scale}
                          onChange={(e) => setTransformations(prev => ({...prev, scale: parseFloat(e.target.value)}))}
                        />
                      </div>
                      
                      <div className="transform-buttons">
                        <button 
                          className={`transform-btn ${transformations.flipHorizontal ? 'active' : ''}`}
                          onClick={() => setTransformations(prev => ({...prev, flipHorizontal: !prev.flipHorizontal}))}
                        >
                          ↔️ Flip H
                        </button>
                        <button 
                          className={`transform-btn ${transformations.flipVertical ? 'active' : ''}`}
                          onClick={() => setTransformations(prev => ({...prev, flipVertical: !prev.flipVertical}))}
                        >
                          ↕️ Flip V
                        </button>
                      </div>
                      
                      <div className="quick-rotate-buttons">
                        <button onClick={() => setTransformations(prev => ({...prev, rotation: prev.rotation - 90}))}>
                          ↶ -90°
                        </button>
                        <button onClick={() => setTransformations(prev => ({...prev, rotation: prev.rotation - 45}))}>
                          ↶ -45°
                        </button>
                        <button onClick={() => setTransformations(prev => ({...prev, rotation: 0}))}>
                          ⟲ Reset
                        </button>
                        <button onClick={() => setTransformations(prev => ({...prev, rotation: prev.rotation + 45}))}>
                          ↷ +45°
                        </button>
                        <button onClick={() => setTransformations(prev => ({...prev, rotation: prev.rotation + 90}))}>
                          ↷ +90°
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {activeTab === 'basic' && (
                    <div className="basic-controls">
                      <h4>Basic Adjustments</h4>
                      
                      <div className="correction-group">
                        <label>Brightness: {colorCorrections.brightness}</label>
                        <input
                          type="range"
                          min="-100"
                          max="100"
                          step="1"
                          value={colorCorrections.brightness}
                          onChange={(e) => setColorCorrections(prev => ({...prev, brightness: parseInt(e.target.value)}))}
                        />
                      </div>
                      
                      <div className="correction-group">
                        <label>Contrast: {colorCorrections.contrast}</label>
                        <input
                          type="range"
                          min="-100"
                          max="100"
                          step="1"
                          value={colorCorrections.contrast}
                          onChange={(e) => setColorCorrections(prev => ({...prev, contrast: parseInt(e.target.value)}))}
                        />
                      </div>
                      
                      <div className="correction-group">
                        <label>Saturation: {colorCorrections.saturation.toFixed(2)}</label>
                        <input
                          type="range"
                          min="-1"
                          max="1"
                          step="0.1"
                          value={colorCorrections.saturation}
                          onChange={(e) => setColorCorrections(prev => ({...prev, saturation: parseFloat(e.target.value)}))}
                        />
                      </div>
                      
                      <div className="correction-group">
                        <label>Gamma: {colorCorrections.gamma.toFixed(2)}</label>
                        <input
                          type="range"
                          min="0.5"
                          max="2.0"
                          step="0.1"
                          value={colorCorrections.gamma}
                          onChange={(e) => setColorCorrections(prev => ({...prev, gamma: parseFloat(e.target.value)}))}
                        />
                      </div>
                    </div>
                  )}
                  
                  {activeTab === 'underwater' && (
                    <div className="underwater-controls">
                      <h4>Underwater Corrections</h4>
                      
                      <div className="preset-buttons">
                        <button onClick={() => applyPreset('shallow')}>🏝️ Shallow Water</button>
                        <button onClick={() => applyPreset('deep')}>🌊 Deep Water</button>
                        <button onClick={() => applyPreset('turbid')}>🌫️ Turbid Water</button>
                        <button onClick={() => applyPreset('green-water')}>🌿 Green Water</button>
                      </div>
                      
                      <div className="correction-group">
                        <label>Red Enhancement: {colorCorrections.redEnhancement.toFixed(2)}</label>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.1"
                          value={colorCorrections.redEnhancement}
                          onChange={(e) => setColorCorrections(prev => ({...prev, redEnhancement: parseFloat(e.target.value)}))}
                        />
                      </div>
                      
                      <div className="correction-group">
                        <label>Blue Reduction: {colorCorrections.blueReduction.toFixed(2)}</label>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.1"
                          value={colorCorrections.blueReduction}
                          onChange={(e) => setColorCorrections(prev => ({...prev, blueReduction: parseFloat(e.target.value)}))}
                        />
                      </div>
                      
                      <div className="correction-group">
                        <label>Green Balance: {colorCorrections.greenBalance.toFixed(2)}</label>
                        <input
                          type="range"
                          min="-1"
                          max="1"
                          step="0.1"
                          value={colorCorrections.greenBalance}
                          onChange={(e) => setColorCorrections(prev => ({...prev, greenBalance: parseFloat(e.target.value)}))}
                        />
                      </div>
                      
                      <div className="correction-group">
                        <label>Depth Compensation: {colorCorrections.depthCompensation.toFixed(2)}</label>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.1"
                          value={colorCorrections.depthCompensation}
                          onChange={(e) => setColorCorrections(prev => ({...prev, depthCompensation: parseFloat(e.target.value)}))}
                        />
                      </div>
                      
                      <div className="correction-group">
                        <label>Turbidity Reduction: {colorCorrections.turbidityReduction.toFixed(2)}</label>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.1"
                          value={colorCorrections.turbidityReduction}
                          onChange={(e) => setColorCorrections(prev => ({...prev, turbidityReduction: parseFloat(e.target.value)}))}
                        />
                      </div>
                      
                      <div className="correction-group">
                        <label>Particle Reduction: {colorCorrections.particleReduction}</label>
                        <input
                          type="range"
                          min="0"
                          max="10"
                          step="1"
                          value={colorCorrections.particleReduction}
                          onChange={(e) => setColorCorrections(prev => ({...prev, particleReduction: parseInt(e.target.value)}))}
                        />
                      </div>
                    </div>
                  )}
                  
                  {activeTab === 'advanced' && (
                    <div className="advanced-controls">
                      <h4>Advanced Filters</h4>
                      
                      <div className="correction-group">
                        <label>Color Temperature: {colorCorrections.colorTemperature.toFixed(2)}</label>
                        <input
                          type="range"
                          min="-1"
                          max="1"
                          step="0.1"
                          value={colorCorrections.colorTemperature}
                          onChange={(e) => setColorCorrections(prev => ({...prev, colorTemperature: parseFloat(e.target.value)}))}
                        />
                      </div>
                      
                      <div className="correction-group">
                        <label>Tint: {colorCorrections.tint.toFixed(2)}</label>
                        <input
                          type="range"
                          min="-1"
                          max="1"
                          step="0.1"
                          value={colorCorrections.tint}
                          onChange={(e) => setColorCorrections(prev => ({...prev, tint: parseFloat(e.target.value)}))}
                        />
                      </div>
                      
                      <div className="correction-group">
                        <label>Clarity: {colorCorrections.clarity.toFixed(2)}</label>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.1"
                          value={colorCorrections.clarity}
                          onChange={(e) => setColorCorrections(prev => ({...prev, clarity: parseFloat(e.target.value)}))}
                        />
                      </div>
                      
                      <div className="correction-group">
                        <label>Dehaze: {colorCorrections.dehaze.toFixed(2)}</label>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.1"
                          value={colorCorrections.dehaze}
                          onChange={(e) => setColorCorrections(prev => ({...prev, dehaze: parseFloat(e.target.value)}))}
                        />
                      </div>
                      
                      <div className="correction-group">
                        <label>Noise Reduction: {colorCorrections.noiseReduction}</label>
                        <input
                          type="range"
                          min="0"
                          max="10"
                          step="1"
                          value={colorCorrections.noiseReduction}
                          onChange={(e) => setColorCorrections(prev => ({...prev, noiseReduction: parseInt(e.target.value)}))}
                        />
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="correction-actions">
                  <button
                    className="reset-btn"
                    onClick={resetAllCorrections}
                  >
                    🔄 Reset All
                  </button>
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button
                className="cancel-btn"
                onClick={() => setProcessingModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="apply-btn"
                onClick={applyPreprocessing}
                disabled={isProcessing}
              >
                {isProcessing ? 'Processing...' : 'Apply & Save'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Large Tile Processing Modal */}
<LargeTileProcessing
  isOpen={largeTileModalOpen}
  onClose={() => setLargeTileModalOpen(false)}
  colorCorrections={colorCorrections}
  transformations={transformations}
  applyAllCorrections={applyAllCorrections}
  selectedModel="picogram2"
/>
    </div>
  );
};


export default QuadratPreprocessingPage;